import { Container, Group, Anchor, rem, Text, Stack, PasswordInput, Button, Alert, Badge, Paper } from '@mantine/core';
import { IconChevronLeft, IconInfoCircle, IconCircleCheck, IconCircleX, IconEdit } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useUpdateSiiPassword } from '@api/accounts';
import { useAuth } from '@providers/auth';
import { useState } from 'react';

function BhePage() {
  const { session } = useAuth();
  const isConfigured = session.activeAccount?.sii_password_configured ?? false;
  const [editing, setEditing] = useState(!isConfigured);
  const [saved, setSaved] = useState(false);

  const { mutate: updateSiiPassword, isLoading } = useUpdateSiiPassword({
    onSuccess: () => {
      setSaved(true);
      setEditing(false);
      form.reset();
    },
  });

  const form = useForm({
    initialValues: { sii_password: '', sii_password_confirm: '' },
    validate: {
      sii_password: (value) => (value.trim().length === 0 ? 'La clave es requerida' : null),
      sii_password_confirm: (value, values) =>
        value !== values.sii_password ? 'Las claves no coinciden' : null,
    },
  });

  const handleSubmit = (values: { sii_password: string }) => {
    setSaved(false);
    updateSiiPassword({ sii_password: values.sii_password });
  };

  const handleCancel = () => {
    setEditing(false);
    form.reset();
  };

  return (
    <Container fluid>
      <Group>
        <Anchor
          href="/configuracion"
          underline="never"
          c="dimmed"
          className="d-flex align-items-center"
          fw={700}
        >
          <IconChevronLeft
            style={{
              width: rem(20),
              height: rem(20),
              color: 'var(--mantine-color-dimmed)',
              marginRight: 10,
            }}
          />
          Configuración
        </Anchor>
      </Group>
      <Text fz={28} fw={700}>
        Boletas de Honorarios Electrónicas
      </Text>
      <Text fz={14} c="dimmed" mt={5}>
        Ingresa tu clave tributaria del SII para que el sistema sincronice automáticamente tus
        boletas de honorarios recibidas una vez al día.
      </Text>

      <Stack mt={30} maw={480}>
        <Paper withBorder p="md" radius="md">
          <Group justify="space-between">
            <Stack gap={4}>
              <Text fw={500}>Clave Tributaria SII</Text>
              {isConfigured ? (
                <Badge color="green" leftSection={<IconCircleCheck size={12} />} variant="light">
                  Configurada
                </Badge>
              ) : (
                <Badge color="red" leftSection={<IconCircleX size={12} />} variant="light">
                  No configurada
                </Badge>
              )}
            </Stack>
            {!editing && (
              <Button
                variant="light"
                leftSection={<IconEdit size={16} />}
                onClick={() => { setSaved(false); setEditing(true); }}
              >
                {isConfigured ? 'Actualizar' : 'Configurar'}
              </Button>
            )}
          </Group>
        </Paper>

        {saved && (
          <Alert icon={<IconCircleCheck />} color="green" variant="light">
            Clave guardada correctamente. Se ha iniciado la sincronización del mes actual y los
            últimos 3 meses. Las boletas aparecerán en Recibidos en los próximos minutos.
          </Alert>
        )}

        {editing && (
          <>
            <Alert icon={<IconInfoCircle />} color="blue" variant="light">
              Tu clave se almacena de forma encriptada y se usa exclusivamente para consultar
              loa.sii.cl.
            </Alert>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <PasswordInput
                  label="Clave Tributaria SII"
                  placeholder="Ingresa tu clave del SII"
                  {...form.getInputProps('sii_password')}
                />
                <PasswordInput
                  label="Confirmar Clave"
                  placeholder="Repite tu clave del SII"
                  {...form.getInputProps('sii_password_confirm')}
                />
                <Group>
                  <Button type="submit" loading={isLoading}>
                    Guardar
                  </Button>
                  {isConfigured && (
                    <Button variant="subtle" color="gray" onClick={handleCancel}>
                      Cancelar
                    </Button>
                  )}
                </Group>
              </Stack>
            </form>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default BhePage;
